import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { WorkOrder } from "../models/WorkOrder";
import { getWorkOrders } from "../repositorie/WorkOrdersRepositorie";
import type { WorkOrdersFilters } from "./useWorkOrdersFilters";

export const WORK_ORDERS_QUERY_KEYS = {
	all: ["workOrders"] as const,
	list: (filters: WorkOrdersFilters) =>
		[
			"workOrders",
			"list",
			filters.assetId,
			// The raw URL values, not the parsed dates: Date objects are rebuilt on every render and
			// would never compare equal.
			filters.startParam,
			filters.endParam,
			filters.searchText,
			filters.typeCode,
			filters.statusCode,
			filters.craftId,
			filters.includeExpired,
		] as const,
};

const EMPTY: WorkOrder[] = [];

/**
 * The work orders for the area in the route. `enabled` keeps the request from going out before the
 * area is known, which is what stopped this page asking for the whole plant three times per load.
 */
export const useWorkOrders = (filters: WorkOrdersFilters) => {
	const query = useQuery<WorkOrder[], Error>({
		queryKey: WORK_ORDERS_QUERY_KEYS.list(filters),
		queryFn: ({ signal }) =>
			getWorkOrders(
				{
					firstTime: false,
					error: false,
					assetSearchName: "",
					Start: filters.start,
					End: filters.end,
					SearchText: filters.searchText,
					TypeCode: filters.typeCode,
					WorkOrderStatusCode: filters.statusCode,
					AssetId: filters.assetId,
					CraftId: filters.craftId,
					IncludeExpired: filters.includeExpired,
				},
				signal,
			),
		enabled: filters.assetId !== null,
		keepPreviousData: true,
	});

	return {
		rows: query.data ?? EMPTY,
		isLoading: query.isFetching,
		error: query.error,
		// The grid distinguishes "nothing matched" from "not loaded yet".
		noResults: query.isSuccess && (query.data?.length ?? 0) === 0,
	};
};

/** Refreshes the work order list after a write, from anywhere on the page. */
export const useRefreshWorkOrders = () => {
	const queryClient = useQueryClient();

	return useCallback(
		() =>
			queryClient.invalidateQueries({ queryKey: WORK_ORDERS_QUERY_KEYS.all }),
		[queryClient],
	);
};
